import { useState, useEffect } from "react";
import Icon from "../Icon";
import { BtnPrimary } from "../Atoms";
import { colorJornada, JORNADA_MS } from "../../utils";
import { hhmm, hhmmss } from "../../utils/hora";

// Widget de fichaje contra Odoo.
//
// El empleado conecta su cuenta de Odoo una vez; el backend guarda la sesión
// (7 días) y nunca la contraseña. El fichaje queda firmado en Odoo por él.
//
// Los descansos se registran como salida + entrada, así que una jornada con
// pausa son varios tramos. Por eso el botón es un único toggle.

const decimalAHoras = h => {
  const m = Math.round((h || 0) * 60);
  return `${Math.floor(m / 60)}h ${String(m % 60).padStart(2, "0")}m`;
};

export default function FichajeWidget({ fichaje }) {
  const f = fichaje;
  const [ahora, setAhora] = useState(Date.now());
  const [login, setLogin] = useState("");
  const [pass,  setPass]  = useState("");
  const [conectando, setConectando] = useState(false);

  // Cronómetro del tramo abierto
  useEffect(() => {
    if (f.estado !== "dentro") return;
    const id = setInterval(() => setAhora(Date.now()), 1000);
    return () => clearInterval(id);
  }, [f.estado]);

  // La sesión de Odoo caduca a los 7 días: al volver a la pestaña se revalida.
  useEffect(() => {
    const onFocus = () => f.cargarEstado();
    window.addEventListener("focus", onFocus);
    return () => window.removeEventListener("focus", onFocus);
  }, [f.cargarEstado]); // eslint-disable-line

  const card = {
    background: "var(--surface)", border: "1px solid var(--border)", borderRadius: 13, boxShadow: "var(--shadow-sm)",
    padding: 16, display: "flex", flexDirection: "column", gap: 12,
  };
  const titulo = {
    fontWeight: 700, fontSize: 11, color: "var(--text2)", textTransform: "uppercase",
    letterSpacing: ".07em", display: "flex", alignItems: "center", gap: 6,
  };

  // ── Cargando ────────────────────────────────────────────────────────────────
  if (f.conectado === null) {
    return (
      <div style={card}>
        <div style={titulo}><Icon name="clock" size={13} color="var(--accent)" />Fichaje</div>
        <div style={{ fontSize: 12, color: "var(--text3)" }}>Consultando Odoo…</div>
      </div>
    );
  }

  // ── Sin conectar ────────────────────────────────────────────────────────────
  if (!f.conectado) {
    const enviar = async e => {
      e.preventDefault();
      if (!login || !pass) return;
      setConectando(true);
      const ok = await f.conectar(login, pass);
      setConectando(false);
      if (ok) { setLogin(""); setPass(""); }
      else setPass("");
    };

    return (
      <div style={card}>
        <div style={titulo}><Icon name="clock" size={13} color="var(--accent)" />Fichaje</div>
        <div style={{ fontSize: 12, color: "var(--text2)", lineHeight: 1.5 }}>
          Conecta tu cuenta de Odoo para fichar desde aquí. Tu contraseña no se guarda:
          solo se usa una vez para abrir la sesión.
        </div>
        <form onSubmit={enviar} style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          <input aria-label="Usuario de Odoo"
            type="email" value={login} onChange={e => setLogin(e.target.value)}
            placeholder="usuario@angellinares.com" autoComplete="username"
            style={{ padding: "8px 10px", borderRadius: 8, border: "1px solid var(--border)",
                     background: "var(--surface2)", color: "var(--text)", fontSize: 13, fontFamily: "var(--font)" }} />
          <input aria-label="Contraseña de Odoo"
            type="password" value={pass} onChange={e => setPass(e.target.value)}
            placeholder="Contraseña de Odoo" autoComplete="current-password"
            style={{ padding: "8px 10px", borderRadius: 8, border: "1px solid var(--border)",
                     background: "var(--surface2)", color: "var(--text)", fontSize: 13, fontFamily: "var(--font)" }} />
          <BtnPrimary onClick={enviar} disabled={conectando || !login || !pass}>
            {conectando ? "Conectando…" : "Conectar con Odoo"}
          </BtnPrimary>
        </form>
        <div style={{ fontSize: 11, color: "var(--text3)", display: "flex", alignItems: "center", gap: 5 }}>
          <Icon name="shield-lock" size={11} />
          Si tienes la verificación en dos pasos activada en Odoo, ficha directamente allí.
        </div>
      </div>
    );
  }

  // ── Conectado ───────────────────────────────────────────────────────────────
  const dentro = f.estado === "dentro";
  const transcurrido = dentro && f.desde ? hhmmss(ahora - new Date(f.desde).getTime()) : null;

  // El numero grande es el TOTAL del dia, y el color lo mide igual que la
  // cabecera. Antes el grande era el tramo en curso, y con varios tramos eso
  // confundia: la cifra que se busca de un vistazo es cuanto llevas hoy, no
  // cuanto llevas desde la ultima vuelta del cafe. El tramo sigue estando, en
  // pequeno debajo y en la lista de tramos.
  const cerradasMs = (f.hoy || []).filter(t => t.check_out)
                                  .reduce((s, t) => s + (t.horas || 0), 0) * 3600000;
  const totalDiaMs = cerradasMs + (dentro && f.desde ? ahora - new Date(f.desde).getTime() : 0);
  const colorHoras = colorJornada(totalDiaMs);

  return (
    <div style={card}>
      <div style={titulo}>
        <Icon name="clock" size={13} color="var(--accent)" />Fichaje
        <span style={{ marginLeft: "auto", fontSize: 12, fontWeight: 700,
                       color: dentro ? "var(--ok-text)" : "var(--text3)" }}>
          {dentro ? "Dentro" : "Fuera"}
        </span>
      </div>

      <div style={{ textAlign: "center", padding: "4px 0" }}>
        <div style={{ fontSize: 30, fontWeight: 800, fontVariantNumeric: "tabular-nums",
                      color: dentro ? "var(--text)" : "var(--text3)", letterSpacing: "-.02em" }}>
          {dentro ? hhmmss(totalDiaMs) : decimalAHoras(f.horasHoy)}
        </div>
        <div style={{ fontSize: 11, color: "var(--text3)", marginTop: 2 }}>
          {dentro
            ? <>Trabajado hoy · <span style={{ color: "var(--ok-text)" }}>
                {transcurrido} desde las {hhmm(f.desde, "--:--")}
              </span></>
            : "Trabajado hoy"}
        </div>
      </div>

      <button
        onClick={f.fichar} disabled={f.fichando}
        style={{
          width: "100%", padding: "13px 0", borderRadius: 10, border: "none",
          cursor: f.fichando ? "wait" : "pointer", fontSize: 14, fontWeight: 700, color: "#fff",
          background: dentro ? "#b91c1c" : "#15803d", opacity: f.fichando ? .6 : 1,
          display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
          transition: "opacity .2s",
        }}>
        <Icon name={dentro ? "box-arrow-right" : "box-arrow-in-right"} size={16} color="#fff" />
        {f.fichando ? "Registrando…" : dentro ? "Fichar salida" : "Fichar entrada"}
      </button>

      {f.hoy.length > 0 && (
        <div style={{ borderTop: "1px solid var(--border2)", paddingTop: 10 }}>
          <div style={{ fontSize: 11, color: "var(--text3)", marginBottom: 6, textTransform: "uppercase",
                        letterSpacing: ".06em", fontWeight: 700 }}>
            {/* El total ya esta arriba en grande. Aqui lo que aporta es el
                COLOR, que va de rojo a verde segun se acerca la jornada, asi que
                se rotula "de 8h" para que se lea como progreso y no como un
                segundo total compitiendo con el de arriba. */}
            Tramos de hoy · <span style={{ color: colorHoras, fontWeight:800 }}>
              {decimalAHoras(totalDiaMs / 3600000)}
            </span> de {decimalAHoras(JORNADA_MS / 3600000)}
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
            {f.hoy.map(t => (
              <div key={t.id} style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 12 }}>
                <span style={{ fontVariantNumeric: "tabular-nums", color: "var(--text)" }}>{hhmm(t.check_in, "--:--")}</span>
                <span style={{ color: "var(--text3)" }}>→</span>
                <span style={{ fontVariantNumeric: "tabular-nums",
                               color: t.check_out ? "var(--text)" : "var(--ok-text)" }}>
                  {t.check_out ? hhmm(t.check_out, "--:--") : "en curso"}
                </span>
                <span style={{ marginLeft: "auto", fontSize: 11, color: "var(--text3)" }}>
                  {t.check_out ? decimalAHoras(t.horas) : ""}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      <div style={{ display: "flex", justifyContent: "flex-end" }}>
        <button className="btn" onClick={f.desconectar}
          style={{ minHeight: 24, padding: "2px 6px", background: "none", border: "none", cursor: "pointer",
            fontSize: 11, color: "var(--text3)", textDecoration: "underline", fontFamily: "var(--font)" }}>
          Desconectar Odoo
        </button>
      </div>
    </div>
  );
}
